import { useState } from "react";
import { Newspaper, ChevronDown, Sparkles, MessageCircleQuestion, Volume2, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const LABEL = {
  local: "Local", national: "National", world: "World", business: "Business",
  technology: "Technology", sport: "Sport", science: "Science", health: "Health",
  entertainment: "Entertainment",
};
const ORDER = ["local", "national", "world", "business", "technology", "sport",
               "science", "health", "entertainment"];

export const NewsPanel = ({ news, onTopics, speaker, bare = false, defaultOpen = false }) => {
  const [open, setOpen] = useState(bare || defaultOpen);
  const [savingTopics, setSavingTopics] = useState(false);
  const [askOpen, setAskOpen] = useState("");
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState("");
  const [answers, setAnswers] = useState({});

  if (!news?.scopes || !Object.values(news.scopes).some((rows) => rows?.length > 0)) return null;

  const toggleTopic = async (topic) => {
    const current = news.topics || [];
    setSavingTopics(true);
    try {
      await api.put("/home/news/topics", {
        topics: current.includes(topic) ? current.filter((t) => t !== topic) : [...current, topic],
      });
      await onTopics();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save those topics");
    } finally {
      setSavingTopics(false);
    }
  };

  const askAbout = async (row) => {
    const q = question.trim();
    if (!q || asking) return;
    setAsking(row.url);
    try {
      const { data } = await api.post("/home/news/ask",
        { title: row.title, snippet: row.snippet || "", question: q });
      setAnswers((prev) => ({ ...prev, [row.url]: { q, answer: data.answer } }));
      setQuestion("");
      speaker.play(row.url, async () => data.answer);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "MiLu could not answer that");
    } finally {
      setAsking("");
    }
  };

  return (
    <div className="mt-4" data-testid="home-news">
      {!bare && (
        <button
          data-testid="home-news-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2 rounded-2xl border border-white/10 bg-page px-4 py-3 text-sm transition-colors hover:border-brand/40"
        >
          <Newspaper className="h-4 w-4 text-brand" />
          <span className="flex-1 text-start font-semibold">Today's headlines</span>
          <ChevronDown className={`h-4 w-4 text-ink-soft transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}

      {open && (
        <div className="mt-2 space-y-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="news-topics">
            <span className="text-[11px] uppercase tracking-widest text-ink-soft">Topics</span>
            {(news.all || []).map((topic) => (
              <button
                key={topic}
                data-testid={`news-topic-${topic}`}
                onClick={() => toggleTopic(topic)}
                disabled={savingTopics}
                className={`min-h-[30px] rounded-full border px-3 text-xs capitalize transition-colors disabled:opacity-50 ${
                  (news.topics || []).includes(topic)
                    ? "border-brand bg-brand/15 font-semibold text-brand"
                    : "border-white/10 text-ink-soft hover:border-brand/40"
                }`}
              >
                {topic}
              </button>
            ))}
          </div>

          {ORDER.map((scope) => (news.scopes[scope]?.length ? (
            <div key={scope} data-testid={`news-${scope}`}>
              <p className="text-[11px] uppercase tracking-widest text-ink-soft">
                {LABEL[scope] || scope}
              </p>
              <ul className="mt-1 space-y-1.5">
                {news.scopes[scope].map((row, i) => (
                  <li key={row.url}>
                    <a href={row.url} target="_blank" rel="noreferrer"
                       className="text-sm hover:text-brand" dir="auto">
                      {row.title}
                    </a>
                    <span className="ms-2 text-xs text-ink-soft">{row.source}</span>
                    <button
                      data-testid={`news-ask-${scope}-${i}`}
                      onClick={() => { setAskOpen(askOpen === row.url ? "" : row.url); setQuestion(""); }}
                      className="ms-2 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                    >
                      <MessageCircleQuestion className="h-3.5 w-3.5" /> Ask MiLu
                    </button>

                    {i === 0 && news.explain?.[scope] && (
                      <p className="mt-1 flex gap-1.5 text-xs text-ink-soft"
                         data-testid={`news-explain-${scope}`} dir="auto">
                        <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-brand" />
                        {news.explain[scope]}
                      </p>
                    )}

                    {askOpen === row.url && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="news-ask-form">
                        <input
                          data-testid="news-ask-input"
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && askAbout(row)}
                          placeholder="What do you want to know?"
                          className="min-h-[36px] min-w-0 flex-1 rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
                        />
                        <button
                          data-testid="news-ask-send"
                          onClick={() => askAbout(row)}
                          disabled={Boolean(asking) || !question.trim()}
                          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
                        >
                          {asking === row.url ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Volume2 className="h-3.5 w-3.5" />}
                          Ask
                        </button>
                      </div>
                    )}

                    {answers[row.url] && (
                      <div className="mt-2 rounded-2xl border border-brand/30 bg-brand/5 p-3"
                           data-testid={`news-answer-${scope}-${i}`}>
                        <p className="text-xs font-semibold text-brand" dir="auto">{answers[row.url].q}</p>
                        <p className="mt-1 text-sm" dir="auto">{answers[row.url].answer}</p>
                        <button
                          data-testid="news-answer-replay"
                          onClick={() => speaker.play(row.url, async () => answers[row.url].answer)}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand hover:underline"
                        >
                          {speaker.playing === row.url ? <Square className="h-3.5 w-3.5" />
                            : <Volume2 className="h-3.5 w-3.5" />}
                          {speaker.playing === row.url ? "Stop" : "Hear it again"}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null))}
        </div>
      )}
    </div>
  );
};
